import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DollarSign, TrendingUp, Calendar, PieChart, BarChart3, Activity } from 'lucide-react';
import Chart from 'chart.js/auto';

const DynamicWidget = ({
    type,
    dataSource,
    data,
    isLoading,
    error,
    textColor,
    title
}) => {
    const renderWidget = () => {
        switch (type) {
            case 'portfolio':
                return <PortfolioSummary data={data} isLoading={isLoading} textColor={textColor} />;
            case 'performance':
                return <PerformanceGraph data={data} isLoading={isLoading} error={error} textColor={textColor} />;
            case 'projections':
                return <Projections data={data} isLoading={isLoading} error={error} textColor={textColor} />;
            case 'sectorAllocation':
                return <SectorAllocation data={data} isLoading={isLoading} error={error} textColor={textColor} />;
            case 'marketSentiment':
                return <MarketSentiment data={data} isLoading={isLoading} error={error} textColor={textColor} />;
            case 'assetPerformance':
                return <AssetPerformance data={data} isLoading={isLoading} error={error} textColor={textColor} />;
            default:
                return <div className="p-4 text-center text-gray-400">Unknown widget type</div>;
        }
    };

    return (
        <div
            className="rounded-[14px] relative overflow-hidden h-full"
            style={{
                background: 'linear-gradient(180deg, rgba(0,0,0,0.22), rgba(0,0,0,0.10))',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                padding: '18px'
            }}
        >
            <h3 className="text-lg font-semibold mb-2 text-white tracking-wide" style={{ letterSpacing: '0.2px', color: '#dfe8ff' }}>
                {title || getDefaultTitle(type)}
            </h3>
            {renderWidget()}
        </div>
    );
};

// Helper function to get default title
const getDefaultTitle = (type) => {
    switch (type) {
        case 'portfolio': return 'Portfolio Summary';
        case 'performance': return 'Performance';
        case 'projections': return 'Projections';
        case 'sectorAllocation': return 'Asset Category';
        case 'marketSentiment': return 'Market Sentiment';
        case 'assetPerformance': return 'Asset Performance';
        default: return 'Widget';
    }
};

// Portfolio Summary Widget
const PortfolioSummary = ({ data, isLoading, textColor }) => {
    if (!data) data = { totalAum: 0, ytdReturn: 0, avgYield: 0 };

    const formattedAum = data.totalAum
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(data.totalAum)
        : 'N/A';

    const formattedReturn = data.ytdReturn !== undefined && data.ytdReturn !== null
        ? `${data.ytdReturn > 0 ? '+' : ''}${parseFloat(data.ytdReturn).toFixed(2)}%`
        : 'N/A';

    const returnColor = data.ytdReturn > 0 ? 'text-primary' : data.ytdReturn < 0 ? 'text-red-400' : '';
    const percentageValue = data.ytdReturn !== undefined && data.ytdReturn !== null
        ? Math.min(Math.max(Math.abs(parseFloat(data.ytdReturn)) * 5, 5), 100)
        : 0;

    return (
        <div className='h-[150px]'>
            {isLoading ? (
                <div className="animate-pulse">
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-2"></div>
                    <div className="h-5 bg-white/10 rounded w-1/2"></div>
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <p className="text-xs" style={{ color: 'var(--muted, #9aa6c1)' }}>
                            Current Value of your Portfolio
                        </p>
                        <p className="text-5xl font-extrabold text-white mt-2 mb-2" style={{ lineHeight: '1.0', letterSpacing: '0.5px' }}>
                            {formattedAum}
                        </p>
                    </div>

                    <div className="flex items-baseline gap-2 mb-3">
                        <p className="text-xs" style={{ color: 'var(--muted, #9aa6c1)' }}>
                            Average Yield:
                        </p>
                        <p className="text-sm font-semibold text-white">
                            {data.avgYield ? `${parseFloat(data.avgYield).toFixed(2)}%` : 'N/A'}
                        </p>
                    </div>

                    {data.ytdReturn !== undefined && data.ytdReturn !== null && (
                        <div className="mt-3">
                            <div className="h-4 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                <div
                                    className="h-full"
                                    style={{ 
                                        width: `${percentageValue}%`, 
                                        background: data.ytdReturn >= 0 ? 'linear-gradient(90deg, var(--primary, #10B981), var(--accent, #14B8A6))' : 'linear-gradient(90deg, #EF4444, #DC2626)',
                                        transition: 'width 1s ease-out',
                                        borderRight: '1px solid rgba(0,0,0,0.25)',
                                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)'
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const PerformanceGraph = ({ data, isLoading, error, textColor }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);


    useEffect(() => {
        if (isLoading || error || !data || !chartRef.current) return;

        // Small timeout to ensure DOM is ready
        const timer = setTimeout(() => {
            try {
                const ctx = chartRef.current.getContext('2d');
                if (!ctx) return; // Safety check

                if (chartInstance.current) {
                    chartInstance.current.destroy();
                }

                const processedData = processReturnsData(data);

                const gradient = ctx.createLinearGradient(0, 0, 0, 180);
                gradient.addColorStop(0, 'rgba(17, 170, 27, 0.15)');
                gradient.addColorStop(1, 'rgba(17, 170, 27, 0)');

                chartInstance.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: processedData.labels,
                        datasets: [{
                            label: 'Returns',
                            data: processedData.data,
                            borderColor: '#10B981',
                            backgroundColor: gradient,
                            tension: 0.38,
                            fill: true,
                            pointRadius: 0,
                            borderWidth: 3
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                backgroundColor: 'rgba(12, 16, 24, 0.9)',
                                titleColor: '#fff',
                                bodyColor: '#9aa6c1',
                                borderColor: 'rgba(255, 255, 255, 0.06)',
                                borderWidth: 1,
                                displayColors: false,
                                padding: 12,
                                cornerRadius: 8,
                                callbacks: {
                                    label: (context) => `${context.parsed.y > 0 ? '+' : ''}${context.parsed.y.toFixed(2)}%`
                                }
                            }
                        },
                        scales: {
                            x: {
                                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                                ticks: {
                                    color: 'rgba(154, 166, 193, 0.9)',
                                    font: { size: 10 },
                                    maxRotation: 0
                                },
                                border: { display: false }
                            },
                            y: {
                                grid: { color: 'rgba(255, 255, 255, 0.06)' },
                                ticks: {
                                    callback: (value) => value > 0 ? `+${value}%` : `${value}%`,
                                    color: 'rgba(154, 166, 193, 0.9)',
                                    font: { size: 10 },
                                    maxTicksLimit: 4
                                },
                                border: { display: false }
                            }
                        }
                    }
                });
            } catch (err) {
                // chart creation failed
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, isLoading, error, textColor]);

    const processReturnsData = (data) => {
        if (!data) return { labels: [], data: [] };

        const labels = [];
        const chartData = [];

        if (Array.isArray(data) && data.length > 0) {
            const firstItem = data[0];

            // Case-insensitive key lookup
            const yearKey = Object.keys(firstItem).find(k => k.toLowerCase() === 'year');
            const quarterKey = Object.keys(firstItem).find(k => k.toLowerCase() === 'quarter');
            const returnKey = Object.keys(firstItem).find(k =>
                ['rate_of_return', 'rate_of_returns', 'return', 'returns', 'ror'].includes(k.toLowerCase())
            );


            if (yearKey && quarterKey) {
                const sortedData = [...data].sort((a, b) => {
                    if (a[yearKey] !== b[yearKey]) return a[yearKey] - b[yearKey];
                    return String(a[quarterKey]).localeCompare(String(b[quarterKey]));
                });

                sortedData.forEach(item => {
                    labels.push(`${item[quarterKey]} ${item[yearKey]}`);
                    chartData.push(parseFloat(item[returnKey] ?? 0));
                });
            }
        } else if (typeof data === 'object' && !Array.isArray(data)) {
            const customerId = Object.keys(data)[0];
            if (customerId && data[customerId]) {
                const yearData = data[customerId];

                Object.keys(yearData).sort().forEach(year => {
                    const quarterlyData = yearData[year];
                    quarterlyData.forEach((value, index) => {
                        labels.push(`Q${index + 1} ${year}`);
                        chartData.push(parseFloat(value));
                    });
                });
            }
        }

        return { labels, data: chartData };
    };

    const calculateAverageReturn = () => {
        if (!data) return null;

        let values = [];

        if (Array.isArray(data) && data.length > 0 && 'rate_of_return' in data[0]) {
            values = data.map(item => parseFloat(item.rate_of_return));
        } else if (typeof data === 'object' && !Array.isArray(data)) {
            const customerId = Object.keys(data)[0];
            if (customerId && data[customerId]) {
                Object.values(data[customerId]).forEach(quarterlyData => {
                    values = values.concat(quarterlyData.map(val => parseFloat(val)));
                });
            }
        }

        if (values.length === 0) return null;

        const sum = values.reduce((acc, val) => acc + val, 0);
        return (sum / values.length).toFixed(2);
    };

    const averageReturn = calculateAverageReturn();
    const isPositiveReturn = averageReturn && parseFloat(averageReturn) > 0;

    return (
        <div className="h-[150px] relative">
            {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs flex items-center" style={{ color: 'var(--muted, #9aa6c1)' }}>
                        <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                    </div>
                </div>
            ) : error ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs text-red-400">Data unavailable</div>
                </div>
            ) : !data || (Array.isArray(data) && data.length === 0) ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs" style={{ color: 'var(--muted, #9aa6c1)' }}>No data available</div>
                </div>
            ) : (
                <>
                    {averageReturn && (
                        <div className="absolute top-0 right-0 z-10">
                            <div className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isPositiveReturn ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                                {isPositiveReturn ? '+' : ''}{averageReturn}% avg
                            </div>
                        </div>
                    )}
                    <canvas ref={chartRef}></canvas>
                </>
            )}
        </div>
    );
};

// Projections Widget
const Projections = ({ data, isLoading, error, textColor }) => {
    // Default fallback values
    const fallbackProjection2030 = 3897502;
    const fallbackProjection2040 = 6724009;

    const formatProjection = (value) => {
        if (value === undefined || value === null) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const projection2030 = data?.['2030'] !== undefined ? data['2030'] : fallbackProjection2030;
    const projection2040 = data?.['2040'] !== undefined ? data['2040'] : fallbackProjection2040;

    return (
        <div className='h-[150px] flex flex-col justify-between'>
            {isLoading ? (
                <div className="animate-pulse space-y-3">
                    <div className="h-5 bg-white/10 rounded w-3/4"></div>
                    <div className="h-5 bg-white/10 rounded w-1/2"></div>
                </div>
            ) : (
                <>
                    <div className="flex items-baseline justify-between border-b border-white/[0.06] pb-3">
                        <div>
                            <div className="text-sm font-bold" style={{ color: '#dfe8ff' }}>2030</div>
                        </div>
                        <div className="text-3xl font-extrabold text-white" style={{ letterSpacing: '0.4px' }}>
                            {formatProjection(projection2030)}
                        </div>
                    </div>

                    <div className="flex items-baseline justify-between pb-3">
                        <div>
                            <div className="text-sm font-bold" style={{ color: '#dfe8ff' }}>2040</div>
                        </div>
                        <div className="text-3xl font-extrabold text-white" style={{ letterSpacing: '0.4px' }}>
                            {formatProjection(projection2040)}
                        </div>
                    </div>

                    <div className="text-xs mt-1" style={{ color: 'rgba(154,166,193,0.9)', fontSize: '12px' }}>
                        This is only an estimate. Subject to variables.
                    </div>
                </>
            )}
        </div>
    );
};

const SectorAllocation = ({ data, isLoading, error, textColor }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const [processedData, setProcessedData] = useState([]);
    const [colors, setColors] = useState([]);

    useEffect(() => {
        if (isLoading || error || !data) return;

        // Support both field name shapes from the API
        const valueKey = data[0]?.allocation_value !== undefined ? 'allocation_value' : 'total_investment_value';
        const percentKey = data[0]?.allocation_percent !== undefined ? 'allocation_percent' : null;

        const totalValue = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0);

        // Sort data by investment value (descending)
        const sortedData = [...data].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));

        // Take top 6 sectors and group the rest as "Other"
        let processed = sortedData.slice(0, 6);

        if (sortedData.length > 6) {
            const otherValue = sortedData
                .slice(6)
                .reduce((sum, item) => sum + (item[valueKey] || 0), 0);

            if (otherValue > 0) {
                processed.push({ sector: 'Other', [valueKey]: otherValue });
            }
        }

        // Use pre-computed percent if available, otherwise calculate from totals
        processed = processed.map(item => ({
            ...item,
            percentage: percentKey
                ? parseFloat(item[percentKey])
                : parseFloat(((item[valueKey] / totalValue) * 100).toFixed(1))
        }));
        
        // Define a color palette that matches your theme
        const colorPalette = [
            '#059669', // Green
            '#036848', // Dark Green
            '#55d1aa', // Light Green
            '#26453b', // Forest Green
            '#10b981', // Emerald
            '#6fdcb9', // Mint
            '#94a3b8'  // Gray for "Other"
        ];
        
        setProcessedData(processed);
        setColors(colorPalette);
        
    }, [data, isLoading, error]);

    useEffect(() => {
        if (isLoading || error || !data || !chartRef.current || processedData.length === 0) return;

        // Small timeout to ensure DOM is ready
        const timer = setTimeout(() => {
            try {
                const ctx = chartRef.current.getContext('2d');
                if (!ctx) return; // Safety check

                if (chartInstance.current) {
                    chartInstance.current.destroy();
                }

                const sectors = processedData.map(item => item.sector);
                const percentages = processedData.map(item => item.percentage);

                chartInstance.current = new Chart(ctx, {
                    type: 'doughnut',
                    data: {
                        labels: sectors,
                        datasets: [{
                            data: percentages,
                            backgroundColor: colors,
                            borderColor: 'rgba(255, 255, 255, 0.8)',
                            borderWidth: 1,
                            hoverOffset: 5
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: false
                            },
                            tooltip: {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                titleColor: '#333',
                                bodyColor: '#333',
                                borderColor: '#f1f5f9',
                                borderWidth: 1,
                                displayColors: true,
                                callbacks: {
                                    label: (context) => {
                                        const item = processedData[context.dataIndex];
                                        const value = item.allocation_value ?? item.total_investment_value;
                                        const formattedValue = new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                        }).format(value);
                                        
                                        return `${context.label}: ${context.raw}% (${formattedValue})`;
                                    }
                                }
                            }
                        },
                        cutout: '60%'
                    }
                });
            } catch (err) {
                // chart creation failed
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [processedData, colors, isLoading, error, data]);

    // Function to format sector names for legend
    const formatSectorName = (sector) => {
        if (!sector) return 'Unknown';
        return sector
            .replace(/\s+(Sector|Industry|Services)$/i, '')
            .trim();
    };

    return (
        <div className="h-[150px] relative">
            {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs flex items-center" style={{ color: 'var(--muted, #9aa6c1)' }}>
                        <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                    </div>
                </div>
            ) : error ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs text-red-400">Data unavailable</div>
                </div>
            ) : !data || (Array.isArray(data) && data.length === 0) ? (
                <div className="absolute inset-0 flex items-center justify-center rounded">
                    <div className="text-xs" style={{ color: 'var(--muted, #9aa6c1)' }}>No data available</div>
                </div>
            ) : (
                <div className="flex h-full gap-3">
                    {/* Chart */}
                    <div className="w-[55%]">
                        <canvas ref={chartRef}></canvas>
                    </div>
                    
                    {/* Legend */}
                    <div className="flex-1 flex flex-col justify-center space-y-1.5">
                        {processedData.map((item, index) => (
                            <div key={index} className="flex items-center gap-1.5" title={item.sector}>
                                <div 
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: colors[index] }}
                                ></div>
                                <div className="text-[10px] leading-tight" style={{ color: 'var(--muted, #9aa6c1)' }}>
                                    {formatSectorName(item.sector)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Market Sentiment Widget - Fear & Greed Index
const MarketSentiment = ({ data, isLoading, error, textColor }) => {
    // Parse the data to extract value and label
    const parseData = (data) => {
        if (!data) return { value: 50, label: 'Neutral' };
        
        // If data is already an object with value and label
        if (typeof data === 'object' && data.value !== undefined) {
            return { value: data.value, label: data.label || 'Neutral' };
        }
        
        // If data is a string, try to extract the number
        if (typeof data === 'string') {
            const numberMatch = data.match(/\d+/);
            const value = numberMatch ? parseInt(numberMatch[0]) : 50;
            
            // Extract label
            let label = 'Neutral';
            if (data.toLowerCase().includes('extreme fear')) label = 'Extreme Fear';
            else if (data.toLowerCase().includes('extreme greed')) label = 'Extreme Greed';
            else if (data.toLowerCase().includes('fear')) label = 'Fear';
            else if (data.toLowerCase().includes('greed')) label = 'Greed';
            
            return { value: Math.min(Math.max(value, 0), 100), label };
        }
        
        return { value: 50, label: 'Neutral' };
    };
    
    const { value, label } = parseData(data);
    
    // Calculate gauge rotation (-90deg to 90deg)
    const rotation = (value / 100) * 180 - 90;
    
    // Determine color based on value - using green theme
    const getColor = (val) => {
        if (val <= 20) return '#dc2626'; // Extreme Fear - darker red
        if (val <= 40) return '#ea580c'; // Fear - darker orange
        if (val <= 60) return '#14b8a6'; // Neutral - teal/accent
        if (val <= 80) return '#10b981'; // Greed - primary green
        return '#059669'; // Extreme Greed - deeper green
    };
    
    const color = getColor(value);

    return (
        <div className="h-[150px] flex flex-col items-center justify-center p-3">
            {isLoading ? (
                <div className="animate-pulse w-full space-y-2">
                    <div className="h-3 bg-white/10 rounded w-full"></div>
                    <div className="h-3 bg-white/10 rounded w-5/6"></div>
                    <div className="h-3 bg-white/10 rounded w-4/6"></div>
                </div>
            ) : error ? (
                <div className="text-sm text-red-400">Unable to fetch data</div>
            ) : (
                <>
                    {/* Gauge Container */}
                    <div className="relative w-[140px] h-[72px] mb-1">
                        {/* Gauge Background Arc */}
                        <svg className="w-full h-full" viewBox="0 0 145 80" style={{ overflow: 'visible' }}>
                            {/* Background arc - single unified gradient effect */}
                            <defs>
                                <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" style={{ stopColor: 'rgba(220, 38, 38, 0.15)' }} />
                                    <stop offset="25%" style={{ stopColor: 'rgba(234, 88, 12, 0.15)' }} />
                                    <stop offset="50%" style={{ stopColor: 'rgba(20, 184, 166, 0.15)' }} />
                                    <stop offset="75%" style={{ stopColor: 'rgba(16, 185, 129, 0.15)' }} />
                                    <stop offset="100%" style={{ stopColor: 'rgba(5, 150, 105, 0.15)' }} />
                                </linearGradient>
                            </defs>
                            
                            {/* Background arc */}
                            <path
                                d="M 12 72 A 60 60 0 0 1 133 72"
                                fill="none"
                                stroke="url(#gaugeGradient)"
                                strokeWidth="10"
                                strokeLinecap="round"
                            />
                            
                            {/* Active arc based on value */}
                            <path
                                d="M 12 72 A 60 60 0 0 1 133 72"
                                fill="none"
                                stroke={color}
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray="190"
                                strokeDashoffset={190 - (value / 100) * 190}
                                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                            />
                            
                            {/* Center circle */}
                            <circle cx="72.5" cy="72" r="3.5" fill="rgba(255,255,255,0.25)" />
                            
                            {/* Needle */}
                            <line
                                x1="72.5"
                                y1="72"
                                x2="72.5"
                                y2="22"
                                stroke={color}
                                strokeWidth="3"
                                strokeLinecap="round"
                                style={{
                                    transformOrigin: '72.5px 72px',
                                    transform: `rotate(${rotation}deg)`,
                                    transition: 'transform 0.5s ease',
                                    filter: `drop-shadow(0 0 4px ${color}40)`
                                }}
                            />
                            <circle cx="72.5" cy="72" r="5" fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}60)` }} />
                        </svg>
                    </div>
                    
                    {/* Value Display */}
                    <div className="text-center mt-1">
                        <div 
                            className="text-[34px] font-bold leading-none mb-0.5" 
                            style={{ color: color, filter: `drop-shadow(0 0 8px ${color}40)` }}
                        >
                            {value}
                        </div>
                        <div 
                            className="text-[11px] font-medium tracking-wide uppercase" 
                            style={{ color: 'rgba(154, 166, 193, 0.95)' }}
                        >
                            {label}
                        </div>
                    </div>
                    
                    {/* Scale Labels */}
                    <div className="flex justify-between w-[140px] mt-1">
                        <span className="text-[8px]" style={{ color: 'rgba(154, 166, 193, 0.7)' }}>Fear</span>
                        <span className="text-[8px]" style={{ color: 'rgba(154, 166, 193, 0.7)' }}>Greed</span>
                    </div>
                </>
            )}
        </div>
    );
};

const AssetPerformance = ({ data, isLoading, error, textColor }) => {

  // Process the data to handle various formats
  const processedData = useMemo(() => {
    if (!data) return [];
    
    let result = [];
    
    // Handle array data
    if (Array.isArray(data)) {
      result = data.map(item => {
        // Try to extract asset name/symbol
        const asset = item.asset_name || item.asset || item.name || item.symbol || 'Unknown Asset';
        const symbol = item.symbol || '';
        
        // Try to extract performance value
        let performance;
        if (item.performance !== undefined) {
          performance = item.performance;
        } else if (item.return !== undefined) {
          performance = item.return;
        } else if (item.rate_of_return !== undefined) {
          performance = item.rate_of_return;
        } else if (item.change !== undefined) {
          performance = item.change;
        } else {
          // Generate a random performance value if none exists
          performance = (Math.random() * 25 - 10).toFixed(1);
        }
        
        return { asset, symbol, performance: parseFloat(performance) };
      });
    }
    
    // Handle object data (convert to array)
    if (typeof data === 'object' && !Array.isArray(data)) {
      result = Object.entries(data).map(([key, value]) => {
        if (typeof value === 'object') {
          // If value is an object, try to extract performance
          const asset = value.name || value.asset_name || value.asset || key;
          const symbol = value.symbol || '';
          const performance = value.performance || value.return || value.rate_of_return || value.change || 0;
          
          return { asset, symbol, performance: parseFloat(performance) };
        } else {
          // If value is a primitive, assume it's the performance
          return { asset: key, symbol: '', performance: parseFloat(value) };
        }
      });
    }
    
    // Sort by performance (highest to lowest)
    return result.sort((a, b) => b.performance - a.performance);
  }, [data]);

  // Find the max absolute performance value for scaling the bars
  const maxAbsPerformance = useMemo(() => {
    if (!processedData.length) return 15; // Default max if no data
    
    return Math.max(
      15, // Minimum scale
      ...processedData.map(item => Math.abs(item.performance || 0))
    );
  }, [processedData]);

  // Format the performance value
  const formatPerformance = (value) => {
    if (isNaN(value)) {
      return 'N/A';
    }
    
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  return (
    <div className="h-[150px] flex flex-col">
      {isLoading ? (
        <div className="animate-pulse flex-1 flex flex-col justify-between">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center">
              <div className="w-12 h-3 bg-gray-700 rounded mr-2"></div>
              <div className="h-3 bg-gray-700 rounded flex-1"></div>
              <div className="w-10 h-3 bg-gray-700 rounded ml-2"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center text-sm text-red-500">
          Unable to fetch asset data
        </div>
      ) : !processedData || processedData.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
          No asset data available
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-between">
          {processedData.slice(0, 5).map((item, index) => (
            <div key={index} className="flex items-center h-6">
              {/* Y-axis (asset symbol) */}
              <div className="w-12 text-xs font-medium text-gray-300 truncate pr-2" title={item.asset}>
                {item.symbol || item.asset.substring(0, 4)}
              </div>
              
              {/* Bar chart container with fixed positioning */}
              <div className="flex-1 relative">
                {/* Center line (zero point) */}
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-600"></div>
                
                {/* Bar - positioned relative to center */}
                {!isNaN(item.performance) && (
                  <div 
                    className={`absolute top-1/2 h-2 ${
                      item.performance >= 0 
                        ? 'left-1/2 bg-primary rounded-r' 
                        : 'right-1/2 bg-red-600 rounded-l'
                    }`}
                    style={{ 
                      width: `${Math.min(50, Math.abs(item.performance) / maxAbsPerformance * 50)}%`,
                      transform: 'translateY(-50%)'
                    }}
                  ></div>
                )}
              </div>
              
              {/* Performance value */}
              <div className={`w-14 text-right text-xs font-medium ${
                !isNaN(item.performance) ? 
                  (item.performance >= 0 ? 'text-primary' : 'text-red-400') :
                  'text-gray-400'
              } pl-2`}>
                {formatPerformance(item.performance)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



export default DynamicWidget;